import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * How recently the operator must have authenticated, in seconds. Mirrors the
 * backend's window so a token this side accepts is one the server accepts too.
 */
const MAX_AUTH_AGE_SECONDS = 120;

/**
 * The `max_age` sent to Azure AD, in seconds.
 *
 * Azure AD issues the resulting tokens with `exp = auth_time + max_age`, so this value
 * caps their lifetime. That has two consequences:
 *
 * - It must stay comfortably above MAX_AUTH_AGE_SECONDS and the backend's matching
 *   window, or the ID token could expire before the freshness check has accepted it,
 *   turning a valid re-authentication into a rejected commit.
 * - The access token issued alongside it is short-lived by construction and must not
 *   replace the session's own token. `max_age=0` produced a token that was already
 *   expired on arrival, which tripped the app's token-expiry watcher and popped the
 *   "Token Expired" dialog immediately after every commit.
 *
 * The forced prompt comes from the request's `prompt: 'login'` instead, which Azure AD
 * honours regardless of this value, so raising it does not weaken the re-authentication.
 */
const MAX_AGE_SECONDS = 300;

/**
 * The ID token claims this service reads. MSAL types `idTokenClaims` as a bare
 * `object`, and `TokenClaims` is not re-exported by `@azure/msal-browser`, so the
 * one claim that matters here is declared locally.
 */
interface ReauthTokenClaims {
  auth_time?: number;
}

/** The outcome of a re-authentication attempt. */
export type ReauthOutcome =
  | { status: 'success'; reauthToken: string }
  | { status: 'cancelled' }
  | { status: 'identity-mismatch'; expected: string; actual: string }
  | { status: 'stale-auth' }
  | { status: 'failed'; message: string };

/**
 * Forces an interactive Active Directory re-authentication without disturbing the
 * current session, used to gate committing a study (MST-521).
 *
 * Four details make this work, each of which is easy to get wrong:
 *
 * - `prompt: 'login'` negates single sign-on for this one request, so Azure AD asks
 *   for credentials instead of silently honouring the existing session cookie. No
 *   logout is needed, and none should be attempted: `logoutRedirect`/`logoutPopup`
 *   would sign the user out of other tabs and applications.
 * - `max_age` goes through `extraQueryParameters` rather than MSAL's own `maxAge`
 *   field. MSAL never forwards `maxAge` to Azure AD, and its client-side validator
 *   throws unconditionally when `maxAge === 0`, so the field is unusable. Sending
 *   `max_age` is also what makes Azure AD emit the `auth_time` claim that the
 *   backend verifies.
 * - Only the ID token is taken from the result; the access token it comes with is
 *   deliberately discarded. See MAX_AGE_SECONDS for why.
 * - A popup, not a redirect: a redirect leaves the SPA and discards the
 *   un-persisted NgRx store, losing the study being committed. The call must
 *   therefore also be reached synchronously from the click handler, or the browser
 *   blocks the popup.
 */
@Injectable({ providedIn: 'root' })
export class ReauthService {

  constructor(
    private authService: MsalService
  ) { }

  /**
   * Prompts the operator to sign in to Active Directory again.
   *
   * Never errors: every failure is mapped to an outcome so the calling dialog stays
   * usable. On success the returned `reauthToken` (the ID token) is sent with the
   * commit request for the server to verify. The session's own access token is left
   * untouched.
   */
  reauthenticate(): Observable<ReauthOutcome> {
    const previousAccount = this.currentAccount();

    return this.authService.acquireTokenPopup({
      scopes: [...environment.apiConfig.scopes],
      // Passing the account is enough for MSAL to add a login_hint; supplying
      // loginHint as well makes it warn about competing hint parameters.
      account: previousAccount ?? undefined,
      prompt: 'login',
      extraQueryParameters: { max_age: String(MAX_AGE_SECONDS) }
    }).pipe(
      map(result => this.evaluate(result, previousAccount)),
      catchError(error => of(this.toFailure(error)))
    );
  }

  /** The account the operator is currently signed in as, if any. */
  private currentAccount(): AccountInfo | null {
    return this.authService.instance.getActiveAccount()
      ?? this.authService.instance.getAllAccounts()[0]
      ?? null;
  }

  /**
   * Decides whether a completed sign-in authorises the commit, and stores the fresh
   * token only when it does.
   */
  private evaluate(result: AuthenticationResult, previousAccount: AccountInfo | null): ReauthOutcome {
    // Identity is checked before anything is stored. `prompt: 'login'` lets the
    // operator sign in as somebody else, and a commit must be attributed to the
    // person who authenticated, so a different account cannot be allowed to
    // silently take over the session. The backend enforces this too by binding the
    // token's oid to the caller; this check only gives a clearer, faster message.
    if (previousAccount && !this.isSameAccount(previousAccount, result.account)) {
      return {
        status: 'identity-mismatch',
        expected: previousAccount.username,
        actual: result.account?.username ?? 'a different account'
      };
    }

    const authTime = (result.idTokenClaims as ReauthTokenClaims | undefined)?.auth_time;
    if (typeof authTime === 'number') {
      // Measured from auth_time alone: Azure AD backdates iat/nbf by five minutes
      // for clock skew, so those claims are older than auth_time on a fresh token
      // and would make a genuine re-authentication look stale.
      const ageSeconds = Math.floor(Date.now() / 1000) - authTime;
      if (ageSeconds > MAX_AUTH_AGE_SECONDS) {
        return { status: 'stale-auth' };
      }
    } else {
      // Only emitted when the sign-in asks for it via max_age, so its absence points
      // at a configuration problem. The backend rejects the commit in this case, so
      // the request is still allowed to proceed and fail there with its own message.
      console.warn('[BPX] Re-authentication returned no auth_time claim; the commit will be rejected by the server');
    }

    if (!result.idToken) {
      return { status: 'failed', message: 'Active Directory did not return a token. Please try again.' };
    }

    // The session's existing access token is deliberately left in place. Adopting the
    // one issued here would shorten the session to this request's max_age and trip the
    // token-expiry watcher; see MAX_AGE_SECONDS.
    this.authService.instance.setActiveAccount(result.account);

    return { status: 'success', reauthToken: result.idToken };
  }

  /**
   * Compares accounts by `homeAccountId`, falling back to a case-insensitive
   * username match when either id is missing.
   */
  private isSameAccount(previous: AccountInfo, current: AccountInfo | null): boolean {
    if (!current) {
      return false;
    }
    if (previous.homeAccountId && current.homeAccountId) {
      return previous.homeAccountId === current.homeAccountId;
    }
    return !!previous.username
      && previous.username.toLowerCase() === (current.username ?? '').toLowerCase();
  }

  /** Maps an MSAL error to an outcome. */
  private toFailure(error: unknown): ReauthOutcome {
    const errorCode = (error as { errorCode?: string })?.errorCode ?? '';

    if (errorCode === 'user_cancelled') {
      return { status: 'cancelled' };
    }

    if (errorCode === 'popup_window_error' || errorCode === 'empty_window_error') {
      return {
        status: 'failed',
        message: 'The Active Directory sign-in window could not be opened. Allow pop-ups for this site and try again.'
      };
    }

    const message = (error as { errorMessage?: string; message?: string })?.errorMessage
      ?? (error as { message?: string })?.message
      ?? '';

    return {
      status: 'failed',
      message: message
        ? `Active Directory sign-in failed: ${message}`
        : 'Active Directory sign-in failed. Please try again.'
    };
  }
}
